import React, { useCallback, useEffect, useRef, useState } from 'react';
import { throttle } from 'lodash';
import { Checkbox, Dropdown, Menu } from 'antd';
import { format, isSameDay, isToday, isYesterday } from 'date-fns';
import clsx from 'clsx';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { ConversationFilter, ConversationSort, ConversationStatus } from 'types';
import OverflowHorizontalIcon from 'shared/assets/images/overflow-horizontal-icon.svg';
import { selectActiveUser } from 'shared/store/shared-selectors';
import { useAppDispatch, useAppSelector } from 'shared/store';
import { useLocalStorage } from 'shared/utils/local-storage';
import { Button } from 'shared/components/Button';
import { Icon } from 'shared/components/Icon';
import UserAvatar from 'shared/components/UserAvatar';
import Loader from 'shared/components/Loader';
import { BulkConversationAssignButton } from 'shared/components/BulkConversationAssignButton';
import { ConversationItem, FakeConversationItem } from './ConversationItem';
import { useConversationList } from 'shared/hooks/conversations';
import './style.less';
import { setUnseenUnassignedConversations } from 'shared/store/conversation-slice';

interface ConversationListProps {
  openConversation: (visitorId: string, conversationId: number) => void;
}

const ConversationList: React.FC<ConversationListProps> = ({ openConversation }) => {
  const dispatch = useAppDispatch();
  const hasEverConnected = useRef(false);
  const { isConnectedToServer } = useAppSelector(s => s.realtime);
  useEffect(() => {
    if (isConnectedToServer) {
      hasEverConnected.current = true;
    }
  }, [isConnectedToServer]);
  const [size, setSize] = useLocalStorage<'default' | 'collapsed'>({
    key: 'ConversationList:size',
    initialState: 'default',
  });

  const toggleSize = useCallback(() => {
    setSize(s => (s === 'default' ? 'collapsed' : 'default'));
    dispatch(setUnseenUnassignedConversations(false));
  }, [dispatch, setSize]);

  const user = useAppSelector(selectActiveUser);
  const { available } = useAppSelector(s => s.dashboard);

  const [lastHoverTime, setLastHoverTime] = useState(0);
  const isHovered = lastHoverTime > 0;

  const {
    hasMoreConversations,
    isFetchingMoreConversations,
    unreadConversations,
    selectedConversations,
    setSelectedConversations,
    filter,
    setFilter,
    status,
    setStatus,
    sort,
    setSort,
    isBulkOpenClosing,
    selectedConversationsIds,
    unreadConversationsList,
    conversationsData,
    assignedUnreadCount,
    allUnreadCount,
    isLoading,
    loadMoreConversations,
    handleBulkOpenClose,
    handleBulkAssign,
    hasRecentUnseenUnassignedConversations,
  } = useConversationList(isHovered);

  const shouldLightUp = hasRecentUnseenUnassignedConversations || assignedUnreadCount > 0;
  // Set when we mount to only render after initial render
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Track hover time, reset hover time after 5s
  const handleMouseEnter = () => setLastHoverTime(Date.now());
  const handleMouseLeave = () => setLastHoverTime(0);
  const handleMouseMove = useRef(throttle(() => setLastHoverTime(Date.now()), 200));

  useEffect(() => {
    let to = 0;
    if (lastHoverTime > 0) {
      to = window.setTimeout(() => setLastHoverTime(0), 5000);
    }
    return () => clearTimeout(to);
  }, [lastHoverTime]);

  // Infinite scroll
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [sentryRef] = useInfiniteScroll({
    loading: isFetchingMoreConversations,
    hasNextPage: hasMoreConversations,
    onLoadMore: loadMoreConversations,
  });

  let currentTime = 0;

  return (
    <div className={clsx('ConversationList', `ConversationList--${size}`)}>
      {hasEverConnected.current && !isConnectedToServer && (
        <div className="ConversationList__disconnected">
          <Loader inline size="small" />
        </div>
      )}
      <div
        className={clsx(
          'ConversationList__header',
          size == 'collapsed' && shouldLightUp && 'ConversationList__header--unreads',
        )}
        onClick={toggleSize}
      >
        <div className="ConversationList__header-avatar">
          <UserAvatar user={user} size={24} />
          <span
            className={clsx(
              'ConversationList__indicator',
              available && 'ConversationList__indicator--available',
            )}
          />
        </div>
        <Icon icon="inbox" />
        Inbox
        {size === 'collapsed' && assignedUnreadCount > 0 && (
          <div className="ConversationList__header-filter">
            <span className={clsx('ConversationList__header-dot')}>
              {assignedUnreadCount > 99 ? '99+' : assignedUnreadCount}
            </span>
          </div>
        )}
        <div className="ConversationList__header-actions">
          <Button
            className="ConversationList__header-collapse"
            data-icon={size}
            type="ghost"
            icon={<Icon icon="down" />}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              toggleSize();
            }}
          />
        </div>
      </div>
      <div className="ConversationList__content">
        <div className="ConversationList__top">
          <div className="ConversationList__inboxHeaderContainer">
            <div
              className={clsx('ConversationList__inboxFilterWrapper', {
                'ConversationList__inboxFilterWrapper--collapsed':
                  selectedConversationsIds.length > 0,
              })}
            >
              <div className="ConversationList__inboxFilter">
                <button
                  className={clsx(
                    'ConversationList__inboxFilter-option',
                    filter === ConversationFilter.ASSIGNED &&
                      'ConversationList__inboxFilter-option--active',
                  )}
                  onClick={() => {
                    setSelectedConversations({});
                    setFilter(ConversationFilter.ASSIGNED);
                  }}
                >
                  <UserAvatar
                    user={user}
                    size={20}
                    className="ConversationList__inboxFilter-avatar"
                  />
                  For you
                  {assignedUnreadCount > 0 && (
                    <span
                      className={clsx(
                        'ConversationList__inboxFilter-dot',
                        filter === ConversationFilter.ASSIGNED &&
                          'ConversationList__inboxFilter-dot--active',
                      )}
                    >
                      {assignedUnreadCount > 99 ? '99+' : assignedUnreadCount}
                    </span>
                  )}
                </button>
                <button
                  className={clsx(
                    'ConversationList__inboxFilter-option',
                    filter === ConversationFilter.ALL &&
                      'ConversationList__inboxFilter-option--active',
                  )}
                  onClick={() => {
                    setSelectedConversations({});
                    setFilter(ConversationFilter.ALL);
                  }}
                >
                  <Icon icon="usersOutline" />
                  Team Inbox
                  {allUnreadCount > 0 && (
                    <span
                      className={clsx(
                        'ConversationList__inboxFilter-dot',
                        filter === ConversationFilter.ALL &&
                          'ConversationList__inboxFilter-dot--active',
                      )}
                    >
                      {allUnreadCount > 99 ? '99+' : allUnreadCount}
                    </span>
                  )}
                </button>
              </div>
              <Dropdown
                overlay={
                  <div className="ConversationList__otherFilters">
                    <div className="ConversationList__statusFilter">
                      {Object.values(ConversationStatus).map(s => {
                        return (
                          <button
                            key={s}
                            className={clsx(
                              'ConversationList__statusFilter-option',
                              status === s &&
                                'ConversationList__statusFilter-option--active',
                            )}
                            onClick={() => {
                              setSelectedConversations({});
                              setStatus(s);
                            }}
                          >
                            {s}
                          </button>
                        );
                      })}
                    </div>
                    <Menu className="ConversationList__sortFilter">
                      {Object.values(ConversationSort).map(option => {
                        return (
                          <Menu.Item
                            key={option}
                            className="ConversationList__sort-item"
                            onClick={() => {
                              setSelectedConversations({});
                              setSort(option);
                            }}
                          >
                            <Icon
                              icon="checkmark"
                              className={clsx(option === sort && 'active')}
                            />
                            <span className="ConversationList__sort-option">
                              {option} first
                            </span>
                          </Menu.Item>
                        );
                      })}
                    </Menu>
                  </div>
                }
                placement="bottomLeft"
                trigger={['click']}
              >
                <Button type="ghost" className="ConversationList__actions">
                  <OverflowHorizontalIcon className="ConversationList__actions-icon" />
                </Button>
              </Dropdown>
            </div>

            {selectedConversationsIds.length > 0 && (
              <div className="ConversationList__bulkActions">
                <Checkbox
                  indeterminate={
                    selectedConversationsIds.length > 0 &&
                    selectedConversationsIds.length < conversationsData.length
                  }
                  onChange={() => {
                    if (selectedConversationsIds.length < conversationsData.length) {
                      let newIds = {};
                      conversationsData.forEach(c => {
                        newIds = { ...newIds, [c.id]: true };
                      });
                      setSelectedConversations(newIds);
                    } else {
                      setSelectedConversations({});
                    }
                  }}
                  checked={selectedConversationsIds.length === conversationsData.length}
                />
                {selectedConversationsIds.length} of {conversationsData.length} selected
                <BulkConversationAssignButton
                  isDisabled={isBulkOpenClosing}
                  handleBulkAssign={handleBulkAssign}
                  callback={() => setSelectedConversations({})}
                />
                <Button
                  type="ghost"
                  className="ConversationList__bulkActions-close"
                  icon={<Icon icon="checkmark" />}
                  loading={isBulkOpenClosing}
                  onClick={handleBulkOpenClose}
                >
                  {status === ConversationStatus.OPEN ? 'Close' : 'Open'}
                </Button>
              </div>
            )}
          </div>
        </div>
        <div
          className="ConversationList__wrapper"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onMouseMove={handleMouseMove.current}
        >
          <div className="ConversationList__list-inner" ref={containerRef}>
            {hasMounted && conversationsData.length === 0 ? (
              <NoConversations />
            ) : (
              <>
                {!!unreadConversationsList.length && (
                  <>
                    <div
                      className={clsx(
                        'ConversationList__item-date',
                        'ConversationList__item-date--today',
                      )}
                    >
                      Unread
                    </div>
                    <>
                      {unreadConversationsList.map(item => {
                        return (
                          <div
                            key={`unread-${item.id}`}
                            className="ConversationList__item-wrapper"
                            onClick={() => openConversation(item.visitor.id, item.id)}
                            role={'button'}
                            tabIndex={0}
                          >
                            <ConversationItem
                              item={item}
                              selectedConversations={selectedConversations}
                              setSelectedConversations={setSelectedConversations}
                            />
                          </div>
                        );
                      })}
                    </>
                  </>
                )}
                {conversationsData.map((item, index) => {
                  // Filter out items displayed in the unread section
                  if (unreadConversations[item.id]) {
                    return null;
                  }

                  const onClick =
                    'fake' in item
                      ? undefined
                      : (e: React.MouseEvent<HTMLInputElement>) => {
                          if (e.target && 'checked' in e.target) return;
                          openConversation(item.visitor.id, item.id);
                        };
                  const key = 'fake' in item ? `fake-item-${index}` : item.id;

                  const itemTime =
                    'fake' in item
                      ? null
                      : item.dateLastMessage || item.dateEnded || item.dateStarted;

                  const isDifferentDay = itemTime && !isSameDay(currentTime, itemTime);
                  currentTime = itemTime || 0;

                  return (
                    <React.Fragment key={key}>
                      {!!isDifferentDay && (
                        <div
                          className={clsx(
                            'ConversationList__item-date',
                            isToday(itemTime) && 'ConversationList__item-date--today',
                          )}
                        >
                          {isToday(itemTime)
                            ? 'Today'
                            : isYesterday(itemTime)
                            ? 'Yesterday'
                            : format(itemTime, 'eee, MMM d')}
                        </div>
                      )}
                      <div
                        className="ConversationList__item-wrapper"
                        onClick={onClick}
                        role={onClick ? 'button' : undefined}
                        tabIndex={onClick ? 0 : undefined}
                      >
                        {'fake' in item ? (
                          <FakeConversationItem loading={isLoading} />
                        ) : (
                          <ConversationItem
                            item={item}
                            selectedConversations={selectedConversations}
                            setSelectedConversations={setSelectedConversations}
                          />
                        )}
                      </div>
                    </React.Fragment>
                  );
                })}
              </>
            )}
            {(isFetchingMoreConversations || hasMoreConversations) && (
              <div ref={sentryRef} className="ConversationList__item-loader">
                <Loader inline size="small" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

function NoConversations() {
  return (
    <div className="ConversationList__empty">
      <div className="ConversationList__empty-inner">
        <div className="ConversationList__empty-cards">
          <div className="ConversationList__empty-cards-card">
            <div className="ConversationList__empty-cards-card-status" />
            <div className="ConversationList__empty-cards-card-info">
              <div className="ConversationList__empty-cards-card-info-title" />
              <div className="ConversationList__empty-cards-card-info-subtitle" />
            </div>
          </div>
          <div className="ConversationList__empty-cards-card">
            <div className="ConversationList__empty-cards-card-status" />
            <div className="ConversationList__empty-cards-card-info">
              <div className="ConversationList__empty-cards-card-info-title" />
              <div className="ConversationList__empty-cards-card-info-subtitle" />
            </div>
          </div>
        </div>
        <h3 className="ConversationList__empty-title">No conversations</h3>
        <p className="ConversationList__empty-text">
          This list will automatically refresh when a conversation is updated.
        </p>
      </div>
    </div>
  );
}

export default ConversationList;
